import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useDashboardData } from '../useDashboardData';
import { TileHint } from '../TileFrame';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from '../dashboardTokens';
import { formatCountdown, formatWallClock } from '../timeFormat';

// Flow + timer tile (#1157, specs/workshop-dashboard.md): the dashboard's
// spine — every chain step as a vertical bar down the left edge, past
// (reached) filled, current highlighted with the countdown, future muted.
// The countdown runs locally from the payload's timer snapshot; every
// flow/timer socket event refetches the payload (coalesced), re-syncing it.

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 0.6vmin, 14px);
`;

const StepRow = styled.li`
  display: flex;
  align-items: baseline;
  gap: clamp(8px, 1vmin, 22px);
  padding: clamp(2px, 0.4vmin, 10px) 0;
  border-left: clamp(4px, 0.5vmin, 12px) solid
    ${({ $state }) => ({
    past: DASH_COLORS.accent,
    live: DASH_COLORS.live,
    future: DASH_COLORS.tileBorder,
  }[$state])};
  padding-left: clamp(10px, 1.2vmin, 26px);
  opacity: ${({ $state }) => ($state === 'future' ? 0.55 : 1)};
`;

const StepName = styled.span`
  font-size: ${DASH_TYPE.meta};
  font-weight: ${({ $live }) => ($live ? 700 : 500)};
  color: ${({ $live }) => ($live ? DASH_COLORS.text : DASH_COLORS.textDim)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LiveBlock = styled.div`
  border-left: clamp(4px, 0.5vmin, 12px) solid ${DASH_COLORS.live};
  padding: clamp(6px, 0.8vmin, 18px) 0 clamp(6px, 0.8vmin, 18px) clamp(10px, 1.2vmin, 26px);
`;

const Countdown = styled.div`
  font-family: ${DASH_FONTS.numeric};
  font-size: clamp(36px, 7vmin, 190px);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  color: ${({ $warn, $paused }) => {
    if ($paused) return DASH_COLORS.warn;
    return $warn ? DASH_COLORS.error : DASH_COLORS.text;
  }};
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${DASH_TYPE.meta};

  td {
    padding: clamp(6px, 0.8vmin, 16px) clamp(8px, 1vmin, 20px);
    border-bottom: 1px solid ${DASH_COLORS.tileBorder};
    vertical-align: baseline;
  }
  td.time {
    font-family: ${DASH_FONTS.numeric};
    white-space: nowrap;
    color: ${DASH_COLORS.textDim};
  }
  tr.live td { color: ${DASH_COLORS.live}; font-weight: 700; }
  tr.past td { color: ${DASH_COLORS.textDim}; }
`;

function stepState(step, flowActive) {
  if (step.isLive && flowActive) return 'live';
  if (step.reachedAt) return 'past';
  return 'future';
}

function stepLabel(step, language) {
  return step.labels?.[language] || step.name || step.family;
}

/**
 * Remaining ms right now: the payload snapshot aged by the time since the
 * fetch, ticking locally once a second while running.
 */
function useLiveRemaining(timer, lastUpdatedAt) {
  const [, setTick] = useState(0);
  const running = Boolean(timer && !timer.isPaused);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [running]);
  if (!timer || timer.remainingMs == null) return null;
  const drift = timer.isPaused ? 0 : Date.now() - (lastUpdatedAt || Date.now());
  return timer.remainingMs - drift;
}

export function FlowTileBody({ t }) {
  const { language } = useTranslation();
  const { data, lastUpdatedAt } = useDashboardData();
  const flow = data?.flow;
  const remainingMs = useLiveRemaining(data?.timer, lastUpdatedAt);
  const liveRef = useRef(null);

  // Keep the live step in view when the chain outgrows the bar
  const liveStepId = flow?.liveStepId;
  useEffect(() => {
    liveRef.current?.scrollIntoView?.({ block: 'center' });
  }, [liveStepId]);

  if (!flow) return <TileHint>{t('dashboard.noData')}</TileHint>;

  return (
    <>
      {!flow.active && <TileHint>{t('dashboard.notStarted')}</TileHint>}
      <StepList>
        {flow.steps.map((step) => {
          const state = stepState(step, flow.active);
          if (state === 'live') {
            return (
              <li key={step.stepId} ref={liveRef}>
                <LiveBlock>
                  <StepName $live>{stepLabel(step, language)}</StepName>
                  {remainingMs != null && (
                    <Countdown $warn={remainingMs < 60000} $paused={data.timer?.isPaused}>
                      {formatCountdown(remainingMs)}
                    </Countdown>
                  )}
                  {data.timer?.isPaused && <TileHint>{t('dashboard.paused')}</TileHint>}
                </LiveBlock>
              </li>
            );
          }
          return (
            <StepRow key={step.stepId} $state={state}>
              <StepName>{stepLabel(step, language)}</StepName>
            </StepRow>
          );
        })}
      </StepList>
    </>
  );
}

export function FlowTileDetail({ t }) {
  const { language } = useTranslation();
  const { data } = useDashboardData();
  const flow = data?.flow;
  if (!flow) return <TileHint>{t('dashboard.noData')}</TileHint>;

  return (
    <DetailTable>
      <tbody>
        {flow.steps.map((step) => {
          const state = stepState(step, flow.active);
          const start = formatWallClock(step.schedule?.start);
          const end = formatWallClock(step.schedule?.end);
          return (
            <tr key={step.stepId} className={state}>
              <td className="time">{start ? `${start}–${end || ''}` : ''}</td>
              <td>{stepLabel(step, language)}</td>
              <td className="time">
                {step.suggestedDuration != null ? `${step.suggestedDuration} min` : ''}
              </td>
              <td className="time">
                {step.reachedAt
                  ? new Date(step.reachedAt).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })
                  : ''}
              </td>
            </tr>
          );
        })}
      </tbody>
    </DetailTable>
  );
}
